import { useState } from 'react';
import { fetchExportHistory, type FetchHistoryParams } from '../services/exportHistoryApi';
import type {
  ExportHistoryItem,
  HistoryFetchStatus,
  SelectedExportFilter
} from '../types/exportTypes';
import { useExportCache } from './useExportCache';

interface UseExportHistoryParams {
  selectedFilters: SelectedExportFilter[];
  currentPage: number;
  pageSize: number;
}

interface UseExportHistoryReturn {
  exportHistory: ExportHistoryItem[];
  historyFetchStatus: HistoryFetchStatus;
  totalPages: number;
  fetchReportHistory: (page?: number, size?: number, forceRefresh?: boolean) => Promise<void>;
  setTotalPages: (pages: number) => void;
  setCurrentPage: (page: number) => void;
  setAvailableFilters: (filters: any[]) => void;
  setExportHistory: (history: ExportHistoryItem[]) => void;
  setHistoryFetchStatus: (status: HistoryFetchStatus) => void;
}

export const useExportHistory = ({
  selectedFilters,
  currentPage,
  pageSize
}: UseExportHistoryParams): UseExportHistoryReturn => {
  const [exportHistory, setExportHistory] = useState<ExportHistoryItem[]>([]);
  const [historyFetchStatus, setHistoryFetchStatus] = useState<HistoryFetchStatus>({
    isLoading: false,
    error: null,
    data: null
  });
  const [totalPages, setTotalPages] = useState(0);

  const { canUseCache, updateCacheData, restoreFromCache } = useExportCache();

  const fetchReportHistory = async (
    page: number = currentPage,
    size: number = pageSize,
    forceRefresh: boolean = false
  ) => {
    try {
      // Check if we can use cached data for unfiltered requests
      const isUnfilteredRequest = selectedFilters.length === 0;

      if (canUseCache(!isUnfilteredRequest, size, page, forceRefresh)) {
        return restoreFromCache(size, {
          setAvailableFilters: () => {}, // Will be handled by the cache restore
          setTotalPages,
          setCurrentPage: () => {}, // Will be handled by the cache restore
          setExportHistory,
          setHistoryFetchStatus
        });
      }

      setHistoryFetchStatus({
        isLoading: true,
        error: null,
        data: historyFetchStatus.data // Keep existing data while loading
      });

      const params: FetchHistoryParams = {
        page,
        pageSize: size,
        selectedFilters
      };

      const parsedData = await fetchExportHistory(params);

      // Cache the data if this is an unfiltered request
      if (isUnfilteredRequest) {
        updateCacheData(parsedData, size, page);
      }

      // Update pagination
      const totalRecords = parsedData.total_records;
      const recordsPerPage = parsedData.no_of_records_per_page;
      const calculatedTotalPages = Math.ceil(totalRecords / recordsPerPage);
      setTotalPages(calculatedTotalPages);

      // Update export history
      setExportHistory(parsedData.data);

      setHistoryFetchStatus({
        isLoading: false,
        error: null,
        data: parsedData
      });

      return parsedData;
    } catch (error) {
      setHistoryFetchStatus({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch export history',
        data: null
      });
      throw error;
    }
  };

  // Dummy setters for compatibility (these will be handled by the hooks that use this)
  const setAvailableFilters = () => {};

  return {
    exportHistory,
    historyFetchStatus,
    totalPages,
    fetchReportHistory,
    setTotalPages,
    setCurrentPage: () => {}, // Will be handled by parent component
    setAvailableFilters,
    setExportHistory,
    setHistoryFetchStatus
  };
};
